import Border from "../../api/model/snapshots/layers/Border";
import DashPattern from "../../api/model/snapshots/layers/DashPattern";
import IndividualThickness from "../../api/model/snapshots/layers/IndividualThickness";
import { TransformedBorder, TransformedBorderDiff, TransformedIndividualThickness } from "../../model/transform";
import { colorEquals, transformedGradientEquals } from "./color";

import { equals as fillEquals } from "./fill";

function dashPatternEquals(dashPattern?: DashPattern, otherDashPattern?: DashPattern) {
    if (!dashPattern && !otherDashPattern) {
        return true;
    }

    if ((dashPattern && !otherDashPattern) || (!dashPattern && otherDashPattern)) {
        return false;
    }

    return (
        dashPattern!.dash === otherDashPattern!.dash &&
        dashPattern!.gap === otherDashPattern!.gap
    );
}

function individualThicknessEquals(
    individualThickness?: IndividualThickness,
    otherIndividualThickness?: IndividualThickness
): boolean {
    if (!individualThickness && !otherIndividualThickness) {
        return true;
    }

    if ((individualThickness && !otherIndividualThickness) || (!individualThickness && otherIndividualThickness)) {
        return false;
    }

    return (
        individualThickness!.top === otherIndividualThickness!.top &&
        individualThickness!.bottom === otherIndividualThickness!.bottom &&
        individualThickness!.left === otherIndividualThickness!.left &&
        individualThickness!.right === otherIndividualThickness!.right
    );
}

function transformedIndividualThicknessEquals(
    individualThickness?: TransformedIndividualThickness,
    otherIndividualThickness?: TransformedIndividualThickness
): boolean {
    if (!individualThickness && !otherIndividualThickness) {
        return true;
    }

    if ((individualThickness && !otherIndividualThickness) || (!individualThickness && otherIndividualThickness)) {
        return false;
    }

    return (
        individualThickness!.top === otherIndividualThickness!.top &&
        individualThickness!.bottom === otherIndividualThickness!.bottom &&
        individualThickness!.left === otherIndividualThickness!.left &&
        individualThickness!.right === otherIndividualThickness!.right
    );
}

function equals(border: Border, otherBorder: Border): boolean {
    return (
        border.position === otherBorder.position &&
        border.thickness === otherBorder.thickness &&
        individualThicknessEquals(border.individualThickness, otherBorder.individualThickness) &&
        dashPatternEquals(border.dashPattern, otherBorder.dashPattern) &&
        fillEquals(border, otherBorder)
    );
}

function transformedDiff(border: TransformedBorder, otherBorder: TransformedBorder): TransformedBorderDiff {
    const diff: TransformedBorderDiff = {};

    if (border.position !== otherBorder.position) {
        diff.position = { change: {
            oldValues: otherBorder.position,
            newValues: border.position
        } };
    }

    if (border.thickness.singleThicknessTransformed !== otherBorder.thickness.singleThicknessTransformed ||
    !transformedIndividualThicknessEquals(
        border.thickness.individualThickness,
        otherBorder.thickness.individualThickness
    )) {
        diff.thickness = { change: {
            oldValues: otherBorder.thickness,
            newValues: border.thickness
        } };
    }

    if (border.color && !otherBorder.color) {
        diff.color = { add: border.color };
    } else if (!border.color && otherBorder.color) {
        diff.color = { remove: otherBorder.color };
    } else if (!colorEquals(border.color?.value, otherBorder.color?.value)) {
        diff.color = { change: {
            oldValues: otherBorder.color!,
            newValues: border.color!
        } };
    }

    if (border.gradient && !otherBorder.gradient) {
        diff.gradient = { add: border.gradient };
    } else if (!border.gradient && otherBorder.gradient) {
        diff.gradient = { remove: otherBorder.gradient };
    } else if (
        border.gradient &&
        otherBorder.gradient &&
        !transformedGradientEquals(border.gradient, otherBorder.gradient)
    ) {
        diff.gradient = { change: {
            oldValues: otherBorder.gradient!,
            newValues: border.gradient!
        } };
    }

    return diff;
}

export { equals, transformedDiff };
